import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { ApiError, ErrorBody, NetworkError, isApiError } from '../client/errors';
/**
 * Member uploads (round 6 §6 — built 2026-08-02, retransported 2026-08-10).
 *
 * ── The one door a member is allowed through ──────────────────────────────
 * `POST /media/uploads` and `/media/uploads/confirm` are open to any signed-in
 * member. **Everything else under `/media` is not** — `GET /media`,
 * `/media/{id}` and `/orphans` all still refuse, because that route resolves
 * *any* asset by id, a child's photograph included. Nothing in this file
 * reaches for them.
 *
 * ── We do not send a policy ───────────────────────────────────────────────
 * Which allow-list applies is read from the caller's capabilities server-side.
 * The backend's phrasing is worth keeping: "a client-declared policy is a door
 * that widens itself."
 *
 * ── The bytes go to the API, not to storage ───────────────────────────────
 * The original spec had `upload.url` as a presigned S3 `PUT`. On 2026-08-10 the
 * backend reversed that — "a presigned PUT is a bearer credential to write into
 * our bucket, and a URL that reaches a browser has left our control" — so the
 * URL is now always a `POST` to our own API, which proxies to storage.
 *
 * **The path is not ours to know.** Dev's filesystem driver has been answering
 * `/api/storage/local` all along; the S3-backed one answers `/api/storage/upload`
 * and lands whenever the backend deploys it. Nothing here reads the path: the
 * ticket's URL is used verbatim, which is why a transport reversal and a driver
 * swap both cost this file nothing. Do not pattern-match on it.
 *
 * The same is true of the verb, for the same reason — the method is read off the
 * ticket rather than hardcoded. What the reversal DID change is the failure
 * surface: the byte upload now answers in the API's own error envelope, and one
 * of those answers — an expired ticket — is not a failure to show anybody (see
 * `uploadAttachment`).
 */
export const MEMBER_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const MEMBER_MAX_BYTES = 10 * 1024 * 1024;
/**
 * A TSP member's final case study — the second door, opened 2026-08-21.
 *
 * PDF only and 50 MB, which are the server's own numbers rather than a guess:
 * `UPLOAD_POLICY.SUBMISSION` holds `{ document: { mimeTypes: ['application/pdf'],
 * maxBytes: 50 * 1024 * 1024 } }`, and the backend's reasoning for the ceiling
 * is worth keeping — "a twenty-page case study scanned at 300 dpi lands in
 * exactly this range".
 */
export const SUBMISSION_DOCUMENT_TYPES = ['application/pdf'];
export const SUBMISSION_MAX_BYTES = 50 * 1024 * 1024;
export const UPLOAD_PROFILES = {
    image: {
        kind: 'image',
        mimeTypes: MEMBER_IMAGE_TYPES,
        maxBytes: MEMBER_MAX_BYTES,
        typeKey: 'upload.imageTypes',
    },
    caseStudy: {
        kind: 'document',
        mimeTypes: SUBMISSION_DOCUMENT_TYPES,
        maxBytes: SUBMISSION_MAX_BYTES,
        typeKey: 'upload.pdfOnly',
    },
};
export const UploadTicket = z.object({
    assetId: z.string().uuid(),
    /**
     * **Nullable, and the null branch is not an error.** When the same bytes were
     * uploaded before, the server dedupes and there is nothing to send — go
     * straight to confirm. A client that assumed an object here would throw on
     * the second identical photograph, which is a case parents hit.
     */
    upload: z
        .object({
        /**
         * `POST` since the transport moved behind the API, and the enum still
         * admits `PUT` on purpose. Reading this rather than assuming it is what
         * made 2026-08-10's reversal a no-op here; narrowing it to the one value
         * the server currently sends would throw that away to save nothing.
         */
        method: z.enum(['PUT', 'POST']),
        url: z.string(),
        headers: z.record(z.string(), z.string()),
        /** 15 minutes. Past it the URL answers `MEDIA_URL_INVALID`. */
        expiresAt: z.string().datetime(),
    })
        .nullable(),
    deduplicated: z.boolean(),
});
/**
 * What `/confirm` answers.
 *
 * Narrowed to what a member may act on. The full `MediaAsset` carries
 * `storageKey`, `driver` and `uploadPolicy` — storage internals we have no use
 * for and would rather not hold.
 *
 * **`url` is absent from this read for member uploads and that is the
 * guarantee, not an omission.** There is no route to the original bytes, ever;
 * a reader is given a derivative built by a job. So the preview after picking a
 * file comes from the LOCAL blob, never from the response.
 */
export const ConfirmedUpload = z.object({
    id: z.string().uuid(),
    mimeType: z.string(),
    bytes: z.number().int().nonnegative(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
    status: z.enum(['PENDING', 'READY', 'FAILED']),
});
/**
 * Reject what the server would reject, before the round trip.
 *
 * The backend suggests driving the picker from `details.allowed` on the
 * refusal, and that is right for the *message* but cannot be right for the
 * *filter* — there is no refusal to read until after a file has been chosen and
 * sent. So the picker is seeded from the constants above and the server's
 * refusal is still the authority when the two ever disagree: `uploadAttachment`
 * surfaces `details.allowed` and `details.maxBytes` verbatim rather than
 * repeating our copy of them.
 */
export function localRejection(file, profile = 'image') {
    const rules = UPLOAD_PROFILES[profile];
    if (file.size === 0)
        return { key: 'upload.empty' };
    if (file.size > rules.maxBytes) {
        return { key: 'upload.tooLarge', values: { mb: Math.round(rules.maxBytes / 1024 / 1024) } };
    }
    if (!rules.mimeTypes.includes(file.type))
        return { key: rules.typeKey };
    return null;
}
/**
 * A SHA-256 of the file, hex, or `undefined` where the platform will not.
 *
 * `checksum` is optional in the contract and we had never sent one, which meant
 * `deduplicated` could never come back true — a parent attaching the same
 * photograph to a second post re-uploaded every byte of it. Sending it is what
 * turns that branch on, and the branch was already written.
 *
 * Undefined rather than throwing, on ANY failure to compute it. `crypto.subtle`
 * is absent in every insecure context and `Blob.arrayBuffer` is missing from
 * some older embedded webviews — and in both cases the member's photograph
 * should upload slightly worse, not fail to upload at all. Letting an
 * optimisation throw is letting it take down the path it was meant to shorten.
 *
 * The server reads an absent checksum as "cannot dedupe", which is exactly the
 * behaviour every upload had before 2026-08-10.
 */
async function sha256Hex(file) {
    try {
        if (!globalThis.crypto?.subtle || typeof file.arrayBuffer !== 'function')
            return undefined;
        const digest = await globalThis.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
        return Array.from(new Uint8Array(digest))
            .map((byte) => byte.toString(16).padStart(2, '0'))
            .join('');
    }
    catch {
        return undefined;
    }
}
function requestTicket(http, file, checksum, profile) {
    return http.post('/media/uploads', {
        kind: UPLOAD_PROFILES[profile].kind,
        mimeType: file.type,
        bytes: file.size,
        filename: file.name,
        checksum,
    }, { schema: UploadTicket });
}
/**
 * The middle request — the bytes themselves.
 *
 * A bare `fetch`, and it stays one now that the URL is on our own API. The
 * reason changed with the transport: it used to be "do not hand our bearer
 * token to a third party", and it is now the backend's rule that the byte route
 * is *deliberately unauthenticated* — "the HMAC in the URL is the credential and
 * it authorises exactly one storage key." Putting this through `HttpClient`
 * would attach an `Authorization` header the route did not ask for, and would
 * also drag the ticket's URL through our base-URL joining, which it is not.
 *
 * `credentials: 'omit'` says the same thing about cookies. The app has none
 * today, so this is about what the request means rather than what it currently
 * sends.
 */
async function sendBytes(upload, file) {
    const response = await fetch(upload.url, {
        method: upload.method,
        headers: upload.headers,
        // Raw bytes. Not FormData, not base64 — the route reads the request stream.
        body: file,
        credentials: 'omit',
    });
    if (response.ok)
        return;
    throw await byteUploadError(response);
}
/**
 * The refusal, in the shape the rest of the app already switches on.
 *
 * The proxied route answers in the API's own envelope — `MEDIA_URL_INVALID`,
 * `MEDIA_EMPTY`, `MEDIA_TOO_LARGE` — so a failure here carries a reason worth
 * showing. Collapsing all three into one "réessayez" was defensible when the
 * other end was S3 and the body was XML; it is throwing away an answer now.
 */
async function byteUploadError(response) {
    const body = await response
        .json()
        .then((json) => ErrorBody.safeParse(json))
        .catch(() => null);
    if (body?.success)
        return new ApiError(response.status, body.data);
    // No envelope: an edge, a proxy or a truncated response — nothing to switch
    // on, so it reads as transport rather than as a refusal the member caused.
    return new NetworkError(`Byte upload failed with ${String(response.status)}`);
}
/** Expired or mis-signed. Only the first is worth doing anything about. */
function isStaleTicket(error) {
    return isApiError(error) && error.code === 'MEDIA_URL_INVALID';
}
/**
 * Ticket → bytes → confirm.
 *
 * ── Why the retry ─────────────────────────────────────────────────────────
 * A ticket lasts 15 minutes, and the flow it guards is a parent picking a
 * photograph — which is the flow that gets interrupted. Pick a photo, answer
 * the door, come back: the URL is dead and the honest-looking behaviour is to
 * show an error for something the member did nothing wrong to cause. So an
 * expired ticket is re-requested and the bytes are sent again, once.
 *
 * Once, deliberately. A ticket that was minted seconds ago and still answers
 * `MEDIA_URL_INVALID` is a signature fault, not an expiry, and looping on it
 * would re-upload the file until the member gave up. The second one surfaces.
 *
 * The retry re-reads `assetId` off the FRESH ticket rather than confirming the
 * dead one — and can legitimately come back `deduplicated`, because the attempt
 * that "expired" may have landed before the URL went stale.
 *
 * A plain function under the hook so the transport can be driven in a test
 * without a React tree — three requests with a retry between them is the part
 * worth pinning, and none of it is about rendering.
 */
export async function uploadAttachment(http, file, profile = 'image') {
    /*
     * A local refusal is thrown as a `LocalUploadRefusal` rather than as a bare
     * `Error` carrying a French sentence. Callers that pre-check with
     * `localRejection` never see it; the ones that go straight to the upload get
     * something they can translate instead of a message they must print verbatim.
     */
    const local = localRejection(file, profile);
    if (local)
        throw new LocalUploadRefusal(local);
    const checksum = await sha256Hex(file);
    let ticket = await requestTicket(http, file, checksum, profile);
    // No `upload` means deduplicated: the bytes are already there.
    if (ticket.upload) {
        try {
            await sendBytes(ticket.upload, file);
        }
        catch (error) {
            if (!isStaleTicket(error))
                throw error;
            ticket = await requestTicket(http, file, checksum, profile);
            if (ticket.upload)
                await sendBytes(ticket.upload, file);
        }
    }
    await http.post('/media/uploads/confirm', { assetId: ticket.assetId }, {
        schema: ConfirmedUpload,
    });
    return ticket.assetId;
}
export function useUploadAttachment(http, profile = 'image') {
    return useMutation({ mutationFn: (file) => uploadAttachment(http, file, profile) });
}
/** A refusal we made ourselves, before the round trip. */
export class LocalUploadRefusal extends Error {
    refusal;
    constructor(refusal) {
        super(refusal.key);
        this.refusal = refusal;
        this.name = 'LocalUploadRefusal';
    }
}
/** The server's own numbers, when it refuses. Ours are only a first guess. */
export function uploadRefusalMessage(error) {
    // Our own pre-check, thrown by `uploadAttachment`. Already a refusal.
    if (error instanceof LocalUploadRefusal)
        return error.refusal;
    if (!(error instanceof ApiError))
        return null;
    const details = error.details;
    switch (error.code) {
        case 'MEDIA_TYPE_NOT_ALLOWED': {
            const allowed = Array.isArray(details?.allowed) ? details.allowed : [];
            /*
             * The subtype, uppercased — `image/jpeg` → `JPEG`, `application/pdf` →
             * `PDF`. This used to strip the literal `image/` prefix, which was
             * correct while a member could only ever send an image and would have
             * printed `APPLICATION/PDF` the day one could.
             */
            const readable = allowed
                .map((mime) => (mime.split('/')[1] ?? mime).toUpperCase())
                .join(', ');
            return readable ? { key: 'upload.allowed', values: { list: readable } } : { key: 'upload.badType' };
        }
        case 'MEDIA_TOO_LARGE': {
            const max = typeof details?.maxBytes === 'number' ? details.maxBytes : null;
            return max
                ? { key: 'upload.tooLarge', values: { mb: Math.round(max / 1024 / 1024) } }
                : { key: 'upload.tooLargeUnknown' };
        }
        case 'MEDIA_EMPTY':
            return { key: 'upload.empty' };
        case 'MEDIA_URL_INVALID':
            // Reaching a member means the retry in `useUploadAttachment` ALSO came
            // back invalid, so this is not the expiry it usually is — a fresh ticket
            // was just refused. Retrying again is the one thing that will not help.
            return { key: 'upload.expired' };
        case 'ATTACHMENT_NOT_YOURS':
            return { key: 'upload.notYours' };
        case 'ATTACHMENT_DUPLICATED':
            // Refused rather than deduplicated server-side, deliberately: collapsing
            // it silently would answer with a list that is not the one we sent.
            return { key: 'upload.duplicated' };
        case 'MEDIA_ASSET_NOT_MEMBER':
            return { key: 'upload.notAttachable' };
        /*
         * The submission door's own refusal, added with it. A library asset or one
         * of the member's own post attachments offered as a case study is refused
         * by name rather than quietly attached — and an editor enrolled in the
         * cohort lands here too, since their PDF is stamped EDITORIAL upstream.
         */
        case 'MEDIA_ASSET_NOT_SUBMISSION':
            return { key: 'upload.notSubmission' };
        case 'MEDIA_NOT_READY':
            return { key: 'upload.notReady' };
        default:
            return null;
    }
}
//# sourceMappingURL=uploads.js.map